import React, { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, IconButton, List, ListItem, ListItemButton, ListItemText, Paper } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import {
    addMillingMaterialTemplate,
    deleteMillingMaterialTemplate,
    millingJobTypesTemplate,
    millingMaterialsTemplate,
    modifyMillingMaterialTemplate,
} from 'apollo/templates/milling';
import { useMutation, useQuery } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { MillingJobType, MillingMaterial } from 'models/milling';
import AddUpdateMillingMaterialDialog from './add-update-milling-material-dialog.components';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';

const ManageMillingMaterials: FC = () => {
    const { t: translate } = useTranslation(['milling-jobs']);
    const [isAddEditOpen, setIsAddEditOpen] = useState(false);
    const [currentMaterial, setCurrentMaterial] = useState<MillingMaterial | undefined>();
    const [assignMaterialDeleteSnackbarOpen, setAssignMaterialDeleteSnackbarOpen] = useState(false);
    const { showBoundary } = useDenthubErrorBoundary();

    const {
        data: { millingMaterials } = {},
        loading,
        refetch,
    } = useQuery(millingMaterialsTemplate, {
        onError(error) {
            showBoundary(error);
        },
    });

    const { data: { millingJobTypes } = {} } = useQuery(millingJobTypesTemplate, {
        onError(error) {
            showBoundary(error);
        },
    });

    const [addMillingMaterial] = useMutation(addMillingMaterialTemplate, {
        onCompleted: () => {
            refetch();
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [modifyMillingMaterial] = useMutation(modifyMillingMaterialTemplate, {
        onCompleted: () => {
            refetch();
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [deleteMillingMaterial] = useMutation(deleteMillingMaterialTemplate, {
        onCompleted: () => {
            refetch();
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleDeleteMaterialClick = (materialId: string) => {
        if (!millingJobTypes.length) {
            deleteMillingMaterial({ variables: { materialId } });
            return;
        }
        let jobTypeMaterials = millingJobTypes.map((JobType: MillingJobType) => JobType.materials);
        jobTypeMaterials = [].concat(...jobTypeMaterials);
        const materialIndex = jobTypeMaterials?.findIndex(
            (JobMat: MillingMaterial) => JobMat?._id?.toString() === materialId,
        );
        if (jobTypeMaterials?.length > 0 && materialIndex === -1) {
            deleteMillingMaterial({ variables: { materialId } });
        } else {
            setAssignMaterialDeleteSnackbarOpen(true);
            setTimeout(() => {
                setAssignMaterialDeleteSnackbarOpen(false);
            }, 1500);
        }
    };

    const handleAddMaterialClick = () => {
        setCurrentMaterial(undefined);
        setIsAddEditOpen(true);
    };

    const handleEditMaterialClick = (material: MillingMaterial) => {
        setCurrentMaterial(material);
        setIsAddEditOpen(true);
    };

    const handleAddMaterial = (material: MillingMaterial) => {
        addMillingMaterial({ variables: material });
        handleCloseAddEdit();
    };

    const handleUpdateMaterial = (material: MillingMaterial) => {
        modifyMillingMaterial({ variables: { ...material, materialId: material._id } });
        handleCloseAddEdit();
    };

    const handleCloseAddEdit = () => {
        setIsAddEditOpen(false);
    };

    return (
        <Fragment>
            <Paper variant="outlined">
                {!loading && (
                    <List dense>
                        {millingMaterials?.map((material: MillingMaterial) => (
                            <ListItem
                                key={material._id}
                                secondaryAction={
                                    <IconButton
                                        onClick={() => handleDeleteMaterialClick(material._id as string)}
                                        edge="end"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                }
                            >
                                <ListItemButton onClick={() => handleEditMaterialClick(material)}>
                                    <ListItemText primary={material.name} />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                )}
            </Paper>
            <Box mt={1} mb={3}>
                <Button onClick={() => handleAddMaterialClick()} startIcon={<AddIcon />}>
                    {translate('milling-jobs:ADD_MATERIAL')}
                </Button>
            </Box>
            <AddUpdateMillingMaterialDialog
                open={isAddEditOpen}
                material={currentMaterial}
                onAdd={handleAddMaterial}
                onUpdate={handleUpdateMaterial}
                onDialogClose={handleCloseAddEdit}
            />
            {assignMaterialDeleteSnackbarOpen ? (
                <AlertSnackbar type="error" message={translate('milling-jobs:ASSIGN_MATERIAL_DELETE_INFO')} />
            ) : undefined}
        </Fragment>
    );
};

export default ManageMillingMaterials;
